import { type StreamingStateManager } from '@databases/database-ai-first-creation-state';
import type {
  AddFilterToken,
  AddHiddenFieldToken,
  CsvErrorCollector,
  EditFilterToken,
  EditSortToken,
  EditViewSettingToken,
  LayoutToken,
  RemoveFilterToken,
  RemoveHiddenFieldToken,
  RemoveSortToken,
  RemoveViewToken,
  SortToken,
  ViewToken,
} from '@databases/streaming-csv-parser';
import {
  CsvErrorType,
  DatabaseSortDirection,
  CsvErrorCategory,
} from '@databases/streaming-csv-parser';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { createField } from '@databases/utils/database/utils';
import { createView } from '@databases/utils/database/view/view';

import type { AgentLayoutSettingsOptions, DatabaseAgent } from '../agents/database-agent';
import type { ProcessingError } from '../types';
import {
  logValidationErrors,
  MAX_VIEWS_ON_CREATE,
  stringToDatabaseCardWidth,
  stringToDatabaseLayoutType,
} from '../utils';

import type { FieldProcessor } from './field-processor';
import { FilterProcessor } from './filter-processor';

export class ViewProcessor {
  constructor(
    private readonly agent: DatabaseAgent,
    private readonly streamingState: StreamingStateManager,
    private readonly fieldProcessor: FieldProcessor,
    private readonly errorCollector: CsvErrorCollector | undefined,
    private readonly operationType: 'create' | 'edit'
  ) {}

  private addError(message: string, category: CsvErrorCategory): void {
    this.errorCollector?.addError(message, category, CsvErrorType.VALIDATION);
  }

  private addErrors(errors: ProcessingError[], category: CsvErrorCategory): void {
    for (const error of errors) {
      this.addError(error.message, category);
    }
  }

  public handleViewToken(viewToken: ViewToken): void {
    const errors = this.validateView(viewToken);

    if (errors.length === 0) {
      // Check if we've reached the maximum view limit (only during database creation)
      if (
        this.operationType === 'create' &&
        this.agent.getViewCount() >= MAX_VIEWS_ON_CREATE
      ) {
        const limitError = {
          message: `Cannot create more than ${MAX_VIEWS_ON_CREATE} views in a database`,
        };
        logValidationErrors([limitError]);
        this.addErrors([limitError], CsvErrorCategory.VIEW);
        return;
      }

      const view = createView();
      view.name = viewToken.name;

      const isFirstView = this.agent.isViewsEmpty();

      this.agent.addView(view);

      if (isFirstView) {
        this.agent.setDefaultView(view.id);
      }
    } else {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.VIEW);
    }
  }

  public handleLayoutToken(layoutToken: LayoutToken): void {
    const errors = this.validateLayout(layoutToken);
    const viewId = this.agent.getViewIdByName(layoutToken.viewName);

    if (errors.length > 0 || !viewId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.LAYOUT);
      return;
    }

    const settings: AgentLayoutSettingsOptions = {};
    const layoutType = stringToDatabaseLayoutType(layoutToken.type);

    if (layoutType) {
      settings.layoutType = layoutType;
    }

    if (layoutToken.groupField) {
      let fieldId = this.agent.getFieldIdByName(layoutToken.groupField);
      const existingField = fieldId ? this.agent.getFieldById(fieldId) : null;
      let fieldName = layoutToken.groupField;

      if (!fieldId) {
        const field = createField(DatabaseFieldType.SELECT, {
          name: fieldName,
        });
        this.agent.addField(field);
        fieldId = field.id;
      } else if (existingField && existingField.type !== DatabaseFieldType.SELECT) {
        const existingSingleSelectField = this.agent.findExistingSingleSelectField();

        if (existingSingleSelectField) {
          fieldId = existingSingleSelectField.id;
          fieldName = existingSingleSelectField.name;
        } else {
          fieldName = `${layoutToken.groupField} 1`;
          const field = createField(DatabaseFieldType.SELECT, {
            name: fieldName,
          });
          this.agent.addField(field);
          fieldId = field.id;
        }
      }

      this.agent.setFieldMultiple(fieldName, false);
      settings.groupFieldId = fieldId;
    }

    this.agent.updateViewLayout(viewId, settings);

    this.streamingState.markFirstLayoutProcessed();
  }

  public async handleFilterToken(
    filterToken: AddFilterToken | EditFilterToken
  ): Promise<void> {
    const errors = FilterProcessor.validateFilterToken(this.agent, filterToken);

    if (errors.length > 0) {
      logValidationErrors(errors);
      return;
    }

    const viewId = this.agent.getViewIdByName(filterToken.viewName);
    if (!viewId) {
      const viewErrors = [{ message: `View ${filterToken.viewName} not found` }];
      logValidationErrors(viewErrors);
      this.addErrors(viewErrors, CsvErrorCategory.FILTER);
      return;
    }

    const filter = await FilterProcessor.convertFilterTokenToDatabaseFilter(
      this.agent,
      filterToken
    );

    if (!filter) {
      const convertErrors = [
        { message: 'No valid filters could be converted from the filter token' },
      ];
      logValidationErrors(convertErrors);
      this.addErrors(convertErrors, CsvErrorCategory.FILTER);
      return;
    }

    this.agent.setViewFilter(viewId, filter);
  }

  public handleRemoveFilterToken(removeFilterToken: RemoveFilterToken): void {
    const viewId = this.agent.getViewIdByName(removeFilterToken.viewName);

    if (!viewId) {
      const errors = [
        {
          message: `View not found: ${removeFilterToken.viewName}. Cannot remove filter from non-existent view.`,
        },
      ];
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.FILTER);
      return;
    }

    this.agent.clearViewFilter(viewId);
  }

  public handleSortToken(sortToken: SortToken): void {
    const errors = this.validateSort(sortToken);
    const viewId = this.agent.getViewIdByName(sortToken.viewName);
    const fieldId = this.fieldProcessor.validateFieldName(sortToken.fieldName, errors);

    if (errors.length > 0 || !viewId || !fieldId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.SORT);
      return;
    }

    const ascending = sortToken.direction === DatabaseSortDirection.ASC;

    this.agent.addSortToView(viewId, fieldId, ascending);
  }

  public handleEditSortToken(editSortToken: EditSortToken): void {
    const errors = this.validateEditSort(editSortToken);
    const viewId = this.agent.getViewIdByName(editSortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(editSortToken.fieldName);

    if (errors.length > 0 || !viewId || !fieldId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.SORT);
      return;
    }

    const ascending = editSortToken.direction === DatabaseSortDirection.ASC;

    this.agent.updateSort(viewId, fieldId, ascending);
  }

  public handleRemoveSortToken(removeSortToken: RemoveSortToken): void {
    const errors = this.validateRemoveSort(removeSortToken);
    const viewId = this.agent.getViewIdByName(removeSortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(removeSortToken.fieldName);

    if (errors.length > 0 || !viewId || !fieldId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.SORT);
      return;
    }

    this.agent.removeSort(viewId, fieldId);
  }

  public handleRemoveViewToken(removeViewToken: RemoveViewToken): void {
    const errors = this.validateRemoveView(removeViewToken);
    const viewId = this.agent.getViewIdByName(removeViewToken.viewName);

    if (errors.length > 0 || !viewId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.VIEW);
      return;
    }

    this.agent.deleteView(viewId);
  }

  public handleAddHiddenFieldToken(hiddenFieldToken: AddHiddenFieldToken): void {
    const errors = this.validateAddHiddenField(hiddenFieldToken);
    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);

    if (errors.length > 0 || !viewId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.VIEW);
      return;
    }

    const fieldIds = hiddenFieldToken.fieldNames
      .map((fieldName: string) => this.agent.getFieldIdByName(fieldName))
      .filter((fieldId: string | undefined): fieldId is string => !!fieldId);

    if (fieldIds.length > 0) {
      this.agent.hideFields(viewId, fieldIds);
    }
  }

  public handleRemoveHiddenFieldToken(hiddenFieldToken: RemoveHiddenFieldToken): void {
    const errors = this.validateRemoveHiddenField(hiddenFieldToken);
    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);

    if (errors.length > 0 || !viewId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.VIEW);
      return;
    }

    const fieldIds = hiddenFieldToken.fieldNames
      .map((fieldName: string) => this.agent.getFieldIdByName(fieldName))
      .filter((fieldId: string | undefined): fieldId is string => !!fieldId);

    if (fieldIds.length > 0) {
      this.agent.showFields(viewId, fieldIds);
    }
  }

  public handleEditViewSettingToken(editViewSettingToken: EditViewSettingToken): void {
    const errors = this.validateEditViewSetting(editViewSettingToken);
    const viewId = this.agent.getViewIdByName(editViewSettingToken.viewName);
    const value = String(editViewSettingToken.value);

    if (errors.length > 0 || !viewId) {
      logValidationErrors(errors);
      this.addErrors(errors, CsvErrorCategory.VIEW);
      return;
    }

    switch (editViewSettingToken.property) {
      case 'layout': {
        const layoutType = stringToDatabaseLayoutType(value);
        this.agent.updateViewLayout(viewId, { layoutType });
        break;
      }
      case 'name': {
        this.agent.updateViewName(editViewSettingToken.viewName, value);
        break;
      }
      case 'numbered_entries': {
        this.agent.updateViewLayout(viewId, {
          numberedEntries: value === 'true',
        });
        break;
      }
      case 'card_size': {
        const cardWidth = stringToDatabaseCardWidth(value);
        this.agent.updateViewLayout(viewId, { cardWidth });
        break;
      }
      case 'display_field_names': {
        this.agent.updateViewLayout(viewId, { showFieldNames: value === 'true' });
        break;
      }
      case 'group_by_field': {
        const fieldId = this.agent.getFieldIdByName(value);
        this.agent.updateViewLayout(viewId, { groupFieldId: fieldId });
        break;
      }
      case 'view_position': {
        const position = parseInt(value, 10);
        this.agent.reorderView(viewId, position);
        break;
      }
      default: {
        const unknownPropErrors = [
          {
            message: `Unknown property ${editViewSettingToken.property} for view ${editViewSettingToken.viewName}`,
          },
        ];
        logValidationErrors(unknownPropErrors);
        this.addErrors(unknownPropErrors, CsvErrorCategory.VIEW);
        break;
      }
    }
  }

  private validateView(viewToken: ViewToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!viewToken.name) {
      // TODO generate view name if missing
      // https://product-fabric.atlassian.net/browse/CNS-20319
    }

    // if (this.databaseAgent.getViewNames().has(viewToken.name)) {
    // TODO generate unique name if name already exists
    // https://product-fabric.atlassian.net/browse/CNS-20319
    // https://product-fabric.atlassian.net/browse/CTD-5064
    // }

    return errors;
  }

  private validateLayout(layoutToken: LayoutToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!stringToDatabaseLayoutType(layoutToken.type)) {
      errors.push({
        message: `Unsupported layout type ${layoutToken.type}`,
      });
    }

    if (layoutToken.type === DatabaseLayoutType.BOARD) {
      if (!layoutToken.groupField) {
        errors.push({
          message: 'Board layout is missing a groupBy field',
        });
      }
    }

    return errors;
  }

  private validateSort(sortToken: SortToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!sortToken.fieldName) {
      errors.push({
        message: 'Sort field name is missing',
      });
    }

    if (!sortToken.direction) {
      errors.push({
        message: `Sort direction is missing for field ${sortToken.fieldName}`,
      });
    }

    const viewId = this.agent.getViewIdByName(sortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(sortToken.fieldName);

    if (viewId && fieldId && this.agent.hasSortForField(viewId, fieldId)) {
      errors.push({
        message: `Sort already exists for field ${sortToken.fieldName} in view ${sortToken.viewName}`,
      });
    }

    return errors;
  }

  private validateEditSort(editSortToken: EditSortToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!editSortToken.viewName) {
      errors.push({
        message: 'View name is missing',
      });
    }

    if (!editSortToken.fieldName) {
      errors.push({
        message: 'Field name is missing',
      });
    }

    if (!editSortToken.direction) {
      errors.push({
        message: `Sort direction is missing for field ${editSortToken.fieldName}`,
      });
    }

    const viewId = this.agent.getViewIdByName(editSortToken.viewName);
    const fieldId = this.agent.getFieldIdByName(editSortToken.fieldName);

    if (viewId && fieldId && !this.agent.hasSortForField(viewId, fieldId)) {
      errors.push({
        message: `No sort exists for field ${editSortToken.fieldName} in view ${editSortToken.viewName}. Cannot edit non-existent sort.`,
      });
    }

    return errors;
  }

  private validateRemoveSort(removeSortToken: RemoveSortToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!removeSortToken.viewName) {
      errors.push({
        message: 'View name is missing',
      });
    }

    if (!removeSortToken.fieldName) {
      errors.push({
        message: 'Field name is missing',
      });
    }

    return errors;
  }

  private validateRemoveView(removeViewToken: RemoveViewToken): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!removeViewToken.viewName) {
      errors.push({
        message: 'View name is missing',
      });
    }

    if (this.agent.getViewCount() <= 1) {
      errors.push({
        message: 'Cannot delete the only remaining view in the database',
      });
    }

    return errors;
  }

  private validateAddHiddenField(
    hiddenFieldToken: AddHiddenFieldToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!hiddenFieldToken.viewName) {
      errors.push({
        message: 'View name is required for add_hidden_field',
      });
    }

    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);
    if (!viewId) {
      errors.push({
        message: `View not found: ${hiddenFieldToken.viewName}. Cannot add hidden fields to non-existent view.`,
      });
    }

    for (const fieldName of hiddenFieldToken.fieldNames) {
      const fieldId = this.agent.getFieldIdByName(fieldName);
      if (!fieldId) {
        errors.push({
          message: `Field not found: ${fieldName}. Cannot hide non-existent field.`,
          severity: 'warning',
        });
      }
    }

    return errors;
  }

  private validateRemoveHiddenField(
    hiddenFieldToken: RemoveHiddenFieldToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!hiddenFieldToken.viewName) {
      errors.push({
        message: 'View name is required for remove_hidden_field',
      });
    }

    const viewId = this.agent.getViewIdByName(hiddenFieldToken.viewName);
    if (!viewId) {
      errors.push({
        message: `View not found: ${hiddenFieldToken.viewName}. Cannot remove hidden fields from non-existent view.`,
      });
    }

    for (const fieldName of hiddenFieldToken.fieldNames) {
      const fieldId = this.agent.getFieldIdByName(fieldName);
      if (!fieldId) {
        errors.push({
          message: `Field not found: ${fieldName}. Cannot show non-existent field.`,
          severity: 'warning',
        });
      }
    }

    return errors;
  }

  private validateEditViewSetting(
    editViewSettingToken: EditViewSettingToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!editViewSettingToken.viewName) {
      errors.push({
        message: 'View name is required',
      });
      return errors;
    }

    const viewId = this.agent.getViewIdByName(editViewSettingToken.viewName);
    if (!viewId) {
      errors.push({
        message: `View not found: ${editViewSettingToken.viewName}. Cannot edit non-existent view.`,
      });
      return errors;
    }

    const value = String(editViewSettingToken.value);

    switch (editViewSettingToken.property) {
      case 'layout': {
        if (!stringToDatabaseLayoutType(value)) {
          errors.push({
            message: `Invalid layout type: ${value} for view ${editViewSettingToken.viewName}`,
          });
        }
        break;
      }
      case 'name': {
        const existingViewId = this.agent.getViewIdByName(value);
        if (existingViewId && existingViewId !== viewId) {
          errors.push({
            message: `View with name ${value} already exists`,
          });
        }
        break;
      }
      case 'numbered_entries': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (layoutType !== DatabaseLayoutType.TABLE) {
          errors.push({
            message: `numbered_entries can only be set on table views`,
          });
        }

        if (value !== 'true' && value !== 'false') {
          errors.push({
            message: `numbered_entries value must be 'true' or 'false'`,
          });
        }
        break;
      }
      case 'card_size': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (
          layoutType !== DatabaseLayoutType.GALLERY &&
          layoutType !== DatabaseLayoutType.BOARD
        ) {
          errors.push({
            message: `card_size can only be set on gallery or board views`,
          });
        }

        if (!stringToDatabaseCardWidth(value)) {
          errors.push({
            message: `card_size value must be 'Small', 'Medium', or 'Large'`,
          });
        }
        break;
      }
      case 'display_field_names': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (
          layoutType !== DatabaseLayoutType.GALLERY &&
          layoutType !== DatabaseLayoutType.BOARD
        ) {
          errors.push({
            message: `display_field_names can only be set on gallery or board views`,
          });
        }

        if (value !== 'true' && value !== 'false') {
          errors.push({
            message: `display_field_names value must be 'true' or 'false'`,
          });
        }
        break;
      }
      case 'group_by_field': {
        const layoutType = this.agent.getViewLayoutType(viewId);
        if (layoutType !== DatabaseLayoutType.BOARD) {
          errors.push({
            message: `group_by_field can only be set on board views`,
          });
        }

        const fieldId = this.agent.getFieldIdByName(value);
        if (!fieldId) {
          errors.push({
            message: `Field not found: ${value}`,
          });
        }
        break;
      }
      case 'view_position': {
        const position = parseInt(value, 10);
        if (isNaN(position) || position < 0) {
          errors.push({
            message: `view_position must be a non-negative integer (0-indexed)`,
          });
        }

        const totalViews = this.agent.getViewCount();
        if (position >= totalViews) {
          errors.push({
            message: `view_position ${position} is out of bounds. Valid range is 0-${
              totalViews - 1
            }.`,
          });
        }
        break;
      }
    }

    return errors;
  }
}
